// Shared "untitled draft" handling so the question placeholder, the My Drafts
// sidebar name, and the submit-button enable logic all agree on what counts as
// an unnamed draft. A draft's question/title starts unset: the box shows
// UNTITLED_DRAFT_TITLE as a placeholder and the sidebar shows the same name
// until the user types a real question, at which point the submit button can
// enable. Treats both an empty title and the legacy default "Untitled Question"
// (any case) as unset, so existing drafts behave the same way.
export const UNTITLED_DRAFT_TITLE = 'Untitled question';

export const isUntitledDraftTitle = (title) => {
  const normalized = (title || '').trim().toLowerCase();
  return !normalized || normalized === 'untitled question';
};

// Decodes HTML entities in tag-stripped text ("&nbsp;", "&#32;", "&#x20;",
// "&NonBreakingSpace;", "&#85;ntitled"). A textarea's innerHTML is parsed as
// raw text, so nothing in it can become an element or run. Without a DOM only
// the non-breaking-space spellings are decoded.
const decodeHtmlEntities = (text) => {
  if (typeof document === 'undefined') {
    return text.replace(/&(?:nbsp|#0*160|#x0*a0);/gi, ' ');
  }
  const decoder = document.createElement('textarea');
  decoder.innerHTML = text;
  return decoder.value;
};

// The placeholder test for text that came out of stored HTML. Tag stripping
// leaves entities behind and pads inline tags with spaces ("Untitled Q
// uestion"), and none of those is a real question: decode, then compare with
// every whitespace character (non-breaking spaces included) removed.
export const isUntitledQuestionHtmlText = (text) => {
  const normalized = decodeHtmlEntities(String(text || ''))
    .replace(/\s+/g, '')
    .toLowerCase();
  return !normalized || normalized === 'untitledquestion';
};

// The title a document's read-only question card shows in place of its stored
// question, or null when the card should show the stored question as it is.
// A document is created with "Untitled Question" as BOTH its title and its one
// question's text, and typing the question afterwards only rewrites the title
// (AnswersFrame.updateAssignmentTitle -> PUT /assignments/{id}), so the stored
// question text stays the placeholder for the life of the draft. A blank or
// placeholder question therefore means "the title is the question". Mirrors the
// backend's GeminiTaskExecutor.resolveQuestionText. Null when the stored
// question is real (bulk-upload and API-created documents keep their own
// prompt) and when the title is unset too, so a caller never trades the only
// text there is for nothing. Both arguments are plain text (markup stripped).
export const documentTitleForPlaceholderQuestion = (questionText, title) => {
  if (!isUntitledQuestionHtmlText(questionText)) return null;
  const fallback = (title || '').trim();
  return isUntitledQuestionHtmlText(fallback) ? null : fallback;
};

export const documentQuestionIsUnset = (submission) => {
  const questions = submission?.assignment?.questions || [];
  const question = questions[0];
  if (submission?.type !== 'DOCUMENT' || questions.length !== 1 ||
    ['children', 'subQuestions', 'multipartChildren'].some((key) => question?.[key]?.length)) return false;
  return isUntitledQuestionHtmlText(String(question?.question || '').replace(/<[^>]*>/g, '')) &&
    isUntitledDraftTitle(submission?.assignment?.title);
};
